import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Send as SendIcon
} from '@mui/icons-material';

const ProspectionDashboard = ({ results }) => {
  const [prospects, setProspects] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Charger les prospects depuis le localStorage
  useEffect(() => {
    const savedProspects = localStorage.getItem('prospectionProspects');
    if (savedProspects) {
      setProspects(JSON.parse(savedProspects));
    }
  }, []);

  // Sauvegarder les prospects dans le localStorage
  const saveProspects = (newProspects) => {
    setProspects(newProspects);
    localStorage.setItem('prospectionProspects', JSON.stringify(newProspects));
  };

  // Ajouter les nouveaux résultats comme prospects
  useEffect(() => {
    if (results && results.length > 0) {
      const newProspects = results.map(business => ({
        id: business.id,
        name: business.name,
        website: business.website,
        phone: business.phone,
        email: business.email,
        address: business.address,
        type: business.type,
        businessPotential: business.businessPotential,
        websiteAnalysis: business.websiteAnalysis,
        status: 'nouveau',
        priority: business.websiteAnalysis?.overallScore < 50 ? 'haute' : business.websiteAnalysis?.overallScore < 70 ? 'moyenne' : 'basse',
        lastContact: null,
        nextFollowUp: null,
        notes: '',
        actions: []
      }));

      // Fusionner avec les prospects existants
      const existingProspects = prospects.filter(p => !newProspects.find(np => np.id === p.id));
      const mergedProspects = [...existingProspects, ...newProspects];
      saveProspects(mergedProspects);
    }
  }, [results]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'nouveau': return 'default';
      case 'contacté': return 'info';
      case 'intéressé': return 'warning';
      case 'proposition': return 'primary';
      case 'signé': return 'success';
      case 'perdu': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'haute': return 'error';
      case 'moyenne': return 'warning';
      case 'basse': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'nouveau': return 'Nouveau';
      case 'contacté': return 'Contacté';
      case 'intéressé': return 'Intéressé';
      case 'proposition': return 'Proposition';
      case 'signé': return 'Signé';
      case 'perdu': return 'Perdu';
      default: return 'Inconnu';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'haute': return 'Haute';
      case 'moyenne': return 'Moyenne';
      case 'basse': return 'Basse';
      default: return 'Inconnue';
    }
  };

  const handleStatusChange = (prospectId, newStatus) => {
    const updatedProspects = prospects.map(p => 
      p.id === prospectId ? { ...p, status: newStatus } : p
    );
    saveProspects(updatedProspects);
  };

  const handlePriorityChange = (prospectId, newPriority) => {
    const updatedProspects = prospects.map(p => 
      p.id === prospectId ? { ...p, priority: newPriority } : p
    );
    saveProspects(updatedProspects);
  };

  const addAction = (prospectId, action) => {
    const updatedProspects = prospects.map(p => 
      p.id === prospectId ? { 
        ...p, 
        actions: [...p.actions, { ...action, date: new Date().toISOString() }],
        lastContact: new Date().toISOString()
      } : p
    );
    saveProspects(updatedProspects);
  };

  const updateProspect = (prospectId, updates) => {
    const updatedProspects = prospects.map(p => 
      p.id === prospectId ? { ...p, ...updates } : p
    );
    saveProspects(updatedProspects);
  };

  const deleteProspect = (prospectId) => {
    const updatedProspects = prospects.filter(p => p.id !== prospectId);
    saveProspects(updatedProspects);
  };

  // Statistiques
  const stats = {
    total: prospects.length,
    nouveau: prospects.filter(p => p.status === 'nouveau').length,
    contacté: prospects.filter(p => p.status === 'contacté').length,
    intéressé: prospects.filter(p => p.status === 'intéressé').length,
    proposition: prospects.filter(p => p.status === 'proposition').length,
    signé: prospects.filter(p => p.status === 'signé').length,
    perdu: prospects.filter(p => p.status === 'perdu').length,
    haute: prospects.filter(p => p.priority === 'haute').length,
    moyenne: prospects.filter(p => p.priority === 'moyenne').length,
    basse: prospects.filter(p => p.priority === 'basse').length
  };

  const conversionRate = stats.total > 0 ? ((stats.signé / stats.total) * 100).toFixed(1) : 0;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DashboardIcon color="primary" />
        Tableau de Bord de Prospection
      </Typography>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2}>
          <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography variant="body2">Total Prospects</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">{stats.nouveau}</Typography>
              <Typography variant="body2">Nouveaux</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">{stats.intéressé}</Typography>
              <Typography variant="body2">Intéressés</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">{stats.signé}</Typography>
              <Typography variant="body2">Signés</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card sx={{ bgcolor: 'error.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">{stats.haute}</Typography>
              <Typography variant="body2">Priorité Haute</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card sx={{ bgcolor: 'secondary.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4">{conversionRate}%</Typography>
              <Typography variant="body2">Taux Conversion</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barre de progression */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pipeline de Vente
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2">Nouveau</Typography>
            <LinearProgress 
              variant="determinate" 
              value={(stats.nouveau / stats.total) * 100} 
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="body2">{stats.nouveau}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2">Contacté</Typography>
            <LinearProgress 
              variant="determinate" 
              value={(stats.contacté / stats.total) * 100} 
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="body2">{stats.contacté}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2">Intéressé</Typography>
            <LinearProgress 
              variant="determinate" 
              value={(stats.intéressé / stats.total) * 100} 
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="body2">{stats.intéressé}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2">Proposition</Typography>
            <LinearProgress 
              variant="determinate" 
              value={(stats.proposition / stats.total) * 100} 
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="body2">{stats.proposition}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Signé</Typography>
            <LinearProgress 
              variant="determinate" 
              value={(stats.signé / stats.total) * 100} 
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="body2">{stats.signé}</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Liste des prospects */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Liste des Prospects ({prospects.length})
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Entreprise</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Dernier Contact</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prospects.map((prospect) => (
                  <TableRow key={prospect.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{prospect.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {prospect.website}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          📞 {prospect.phone}
                        </Typography>
                        <Typography variant="body2">
                          📧 {prospect.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${prospect.websiteAnalysis?.overallScore || 0}/100`}
                        color={prospect.websiteAnalysis?.overallScore < 50 ? 'error' : 
                               prospect.websiteAnalysis?.overallScore < 70 ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small">
                        <Select
                          value={prospect.status}
                          onChange={(e) => handleStatusChange(prospect.id, e.target.value)}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="nouveau">Nouveau</MenuItem>
                          <MenuItem value="contacté">Contacté</MenuItem>
                          <MenuItem value="intéressé">Intéressé</MenuItem>
                          <MenuItem value="proposition">Proposition</MenuItem>
                          <MenuItem value="signé">Signé</MenuItem>
                          <MenuItem value="perdu">Perdu</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small">
                        <Select
                          value={prospect.priority}
                          onChange={(e) => handlePriorityChange(prospect.id, e.target.value)}
                          sx={{ minWidth: 100 }}
                        >
                          <MenuItem value="haute">Haute</MenuItem>
                          <MenuItem value="moyenne">Moyenne</MenuItem>
                          <MenuItem value="basse">Basse</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {prospect.lastContact ? 
                          new Date(prospect.lastContact).toLocaleDateString() : 
                          'Jamais'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Voir détails">
                          <IconButton 
                            size="small"
                            onClick={() => {
                              setSelectedProspect(prospect);
                              setDialogOpen(true);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ajouter action">
                          <IconButton 
                            size="small"
                            onClick={() => {
                              addAction(prospect.id, {
                                type: 'email',
                                description: 'Email de prospection envoyé'
                              });
                            }}
                          >
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton 
                            size="small"
                            onClick={() => deleteProspect(prospect.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog de détails */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedProspect && (
          <>
            <DialogTitle>
              Détails - {selectedProspect.name}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Informations
                  </Typography>
                  <Typography variant="body2">
                    <strong>Site web :</strong> {selectedProspect.website}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Téléphone :</strong> {selectedProspect.phone}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email :</strong> {selectedProspect.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Adresse :</strong> {selectedProspect.address}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Score :</strong> {selectedProspect.websiteAnalysis?.overallScore}/100
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Actions
                  </Typography>
                  {selectedProspect.actions.map((action, index) => (
                    <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>{action.type}</strong> - {action.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(action.date).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>
                Fermer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ProspectionDashboard;